import React, {
    CSSProperties,
    ElementType,
    MouseEvent,
    Ref,
    ReactNode,
    useRef,
    useEffect,
    useState,
    useCallback,
    forwardRef,
    useImperativeHandle,
} from 'react';

import './ScrollContainer.css';

const SCROLL_BUFFER = 100;
const LEFT_BUTTON = 0;

export interface ScrollEvent {
    external: boolean;
}

interface Props {
    vertical?: boolean;
    horizontal?: boolean;
    hideScrollbars?: boolean;
    activationDistance?: number;
    children?: ReactNode;
    onStartScroll?: (event: ScrollEvent) => void;
    onScroll?: (event: ScrollEvent) => void;
    onEndScroll?: (event: ScrollEvent) => void;
    onClick?: (event: MouseEvent) => void;
    className?: string;
    draggingClassName?: string;
    style?: CSSProperties;
    ignoreElements?: string;
    nativeMobileScroll?: boolean;
    component?: ElementType;
    innerRef?: Ref<HTMLElement>;
    stopPropagation?: boolean;
    buttons?: number[];
}

const ScrollContainer = forwardRef<HTMLElement, Props>((props, ref) => {
    const {
        vertical,
        horizontal,
        hideScrollbars,
        activationDistance = 10,
        children,
        onStartScroll,
        onScroll,
        onEndScroll,
        onClick,
        className,
        draggingClassName,
        style = {},
        ignoreElements,
        nativeMobileScroll,
        component: Component = 'div',
        stopPropagation,
        buttons = [LEFT_BUTTON],
    } = props;

    const containerRef = useRef<HTMLElement>(null);
    const [isScrolling, setIsScrolling] = useState(false);
    const [isScrollStarted, setIsScrollStarted] = useState(false);
    const [isPressed, setIsPressed] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isInternal, setIsInternal] = useState(false);

    const scrollLeftRef = useRef<number>();
    const scrollTopRef = useRef<number>();
    const clientXRef = useRef<number>();
    const clientYRef = useRef<number>();

    // Expose container element via ref
    useImperativeHandle(ref, () => containerRef.current as HTMLElement);
    useImperativeHandle(props.innerRef, () => containerRef.current as HTMLElement);

    // Check if mobile device
    const isMobileDevice = useCallback(() => {
        return typeof window.orientation !== 'undefined' || navigator.userAgent.indexOf('IEMobile') !== -1;
    }, []);

    // Check if element is draggable
    const isDraggable = useCallback((target: EventTarget) => {
        if (!ignoreElements) return true;

        const element = containerRef.current;
        if (!element) return true;

        const closest = (target as Element).closest(ignoreElements);
        return closest === null || !element.contains(closest);
    }, [ignoreElements]);

    // Check if container is scrollable
    const isScrollable = useCallback(() => {
        const container = containerRef.current;
        return container && (
            container.scrollWidth > container.clientWidth ||
            container.scrollHeight > container.clientHeight
        );
    }, []);

    // Check if target is a drag element
    const isDragElement = useCallback((target: EventTarget) => {
        const element = target as HTMLElement;
        // Проверяем, есть ли у элемента или его родителей классы для drag
        return element.closest('[draggable="true"], .drag-handle, .draggable') !== null;
    }, []);

    // Process scroll end
    const processEnd = useCallback(() => {
        if (onEndScroll) {
            onEndScroll({
                external: !isInternal,
            });
        }

        setIsPressed(false);
        setIsScrollStarted(false);
        setIsScrolling(false);
        setIsInternal(false);

        document.body.classList.remove('dragging');
    }, [onEndScroll, isInternal]);

    // Process scroll start
    const processStart = useCallback((changeCursor = true) => {
        setIsScrollStarted(true);

        if (changeCursor) {
            document.body.classList.add('dragging');
        }

        if (onStartScroll) {
            onStartScroll({
                external: !isInternal,
            });
        }
    }, [onStartScroll, isInternal]);

    // Process native scroll
    const processScroll = useCallback((e: Event) => {
        const container = containerRef.current;
        if (!container) return;

        if (container.scrollLeft !== scrollLeftRef.current || container.scrollTop !== scrollTopRef.current) {
            setIsScrolling(true);

            if (isScrollStarted) {
                if (onScroll) {
                    onScroll({
                        external: !isInternal,
                    });
                }
            } else {
                processStart(false);
            }

            scrollLeftRef.current = container.scrollLeft;
            scrollTopRef.current = container.scrollTop;
        }
    }, [isScrollStarted, isInternal, onScroll, processStart]);

    // Process move event
    const processMove = useCallback((e: Event, newClientX: number, newClientY: number) => {
        const container = containerRef.current;
        if (!container) return;

        if (!isScrollStarted) {
            if (
                (horizontal && Math.abs(newClientX - clientXRef.current!) > activationDistance) ||
                (vertical && Math.abs(newClientY - clientYRef.current!) > activationDistance)
            ) {
                clientXRef.current = newClientX;
                clientYRef.current = newClientY;
                processStart();
            }
        } else {
            if (horizontal) {
                container.scrollLeft -= newClientX - clientXRef.current!;
            }
            if (vertical) {
                container.scrollTop -= newClientY - clientYRef.current!;
            }

            if (onScroll) {
                onScroll({ external: !isInternal });
            }

            clientXRef.current = newClientX;
            clientYRef.current = newClientY;
            scrollLeftRef.current = container.scrollLeft;
            scrollTopRef.current = container.scrollTop;
        }
    }, [isScrollStarted, horizontal, vertical, activationDistance, onScroll, isInternal, processStart]);

    // Process click/touch start
    const processClick = useCallback((e: Event, clientX: number, clientY: number) => {
        const container = containerRef.current;
        if (!container) return;

        scrollLeftRef.current = container.scrollLeft;
        scrollTopRef.current = container.scrollTop;
        clientXRef.current = clientX;
        clientYRef.current = clientY;
        setIsPressed(true);
    }, []);

    // Event handlers
    const handleScroll = useCallback((e: React.UIEvent) => {
        processScroll(e.nativeEvent);
    }, [processScroll]);

    const handleTouchStart = useCallback((e: TouchEvent) => {
        if (e.target && isDraggable(e.target) && !isDragElement(e.target)) {
            setIsInternal(true);
            if (nativeMobileScroll && isScrolling) {
                setIsPressed(true);
            } else {
                const touch = e.touches[0];
                processClick(e, touch.clientX, touch.clientY);
                if (!nativeMobileScroll && stopPropagation) {
                    e.stopPropagation();
                }
            }
        }
    }, [isDraggable, isDragElement, nativeMobileScroll, isScrolling, processClick, stopPropagation]);

    const handleTouchEnd = useCallback((e: TouchEvent) => {
        if (isPressed) {
            if (isScrollStarted && (!isScrolling || !nativeMobileScroll)) {
                processEnd();
            } else {
                setIsPressed(false);
            }
        }
    }, [isPressed, isScrollStarted, isScrolling, nativeMobileScroll, processEnd]);

    const handleTouchMove = useCallback((e: TouchEvent) => {
        if (isPressed && (!nativeMobileScroll || !isMobile) && !isDragElement(e.target as EventTarget)) {
            const touch = e.touches[0];
            if (touch) {
                processMove(e, touch.clientX, touch.clientY);
            }
            e.preventDefault();
            if (stopPropagation) {
                e.stopPropagation();
            }
        }
    }, [isPressed, nativeMobileScroll, isMobile, isDragElement, processMove, stopPropagation]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        const target = e.target as HTMLElement;

        // Проверяем, является ли элемент drag-элементом
        if (isDragElement(target)) {
            // Не обрабатываем события для drag-элементов
            return;
        }

        const interactiveElements = ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'];
        if (interactiveElements.includes(target.tagName) || target.isContentEditable) {
            return;
        }

        if (isDraggable(target) && isScrollable()) {
            setIsInternal(true);
            if (buttons.indexOf(e.button) !== -1) {
                processClick(e.nativeEvent, e.clientX, e.clientY);
                e.preventDefault();
                if (stopPropagation) {
                    e.stopPropagation();
                }
            }
        }
    }, [isDraggable, isDragElement, isScrollable, buttons, processClick, stopPropagation]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        // Проверяем, является ли цель события drag-элементом

        if (isDragElement(e.target as EventTarget)) {
            return; // Не обрабатываем движение для drag-элементов
        }

        if (isPressed) {
            processMove(e.nativeEvent, e.clientX, e.clientY);
            e.preventDefault();
            if (stopPropagation) {
                e.stopPropagation();
            }
        }
    }, [isPressed, isDragElement, processMove, stopPropagation]);

    const handleMouseUp = useCallback((e: React.MouseEvent) => {
        // Проверяем, является ли цель события drag-элементом
        // if (isDragElement(e.target as EventTarget)) {
        //     return; // Не обрабатываем отпускание для drag-элементов
        // }

        if (isPressed) {
            if (isScrollStarted) {
                processEnd();
            } else {
                setIsInternal(false);
                setIsPressed(false);
                if (onClick) {
                    onClick(e);
                }
            }
            e.preventDefault();
            if (stopPropagation) {
                e.stopPropagation();
            }
        }
    }, [isPressed, isScrollStarted, isDragElement, processEnd, onClick, stopPropagation]);

    const calculateScrollSpeed = (distance: number) => {
        return Math.min(20, distance / 3);
    };

    const scrollAnimationRef = useRef<number | null>(null);

    const stopDrag = (e: DragEvent) => {
        if (scrollAnimationRef.current)
            cancelAnimationFrame(scrollAnimationRef.current);
    }

    const handleDrag = useCallback((e: DragEvent) => {
        if (!containerRef.current) return;

        if (scrollAnimationRef.current) {
            cancelAnimationFrame(scrollAnimationRef.current);
        }

        const animateScroll = () => {
            const container = containerRef.current;
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const mouseX = e.clientX;
            const mouseY = e.clientY;

            let scrollX = 0;
            let scrollY = 0;

            const distanceToLeft = mouseX - rect.left;
            const distanceToRight = rect.right - mouseX;
            const distanceToTop = mouseY - rect.top;
            const distanceToBottom = rect.bottom - mouseY;

            if (distanceToLeft < SCROLL_BUFFER) {
                scrollX = -calculateScrollSpeed(SCROLL_BUFFER - distanceToLeft);
            } else if (distanceToRight < SCROLL_BUFFER) {
                scrollX = calculateScrollSpeed(SCROLL_BUFFER - distanceToRight);
            }
            
            if (distanceToTop < SCROLL_BUFFER) {
                scrollY = -calculateScrollSpeed(SCROLL_BUFFER - distanceToTop);
            } else if (distanceToBottom < SCROLL_BUFFER) {
                scrollY = calculateScrollSpeed(SCROLL_BUFFER - distanceToBottom);
            }

            if (scrollX !== 0 || scrollY !== 0) {
                container.scrollLeft += scrollX;
                container.scrollTop += scrollY;
                scrollAnimationRef.current = requestAnimationFrame(animateScroll);
            }
        };

        scrollAnimationRef.current = requestAnimationFrame(animateScroll);
    }, [])

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        window.addEventListener('drag', handleDrag as EventListener);
        window.addEventListener('dragend', stopDrag as EventListener);

        window.addEventListener('mouseup', handleMouseUp as any);
        window.addEventListener('mousemove', handleMouseMove as any);
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', handleTouchEnd);

        container.addEventListener('touchstart', handleTouchStart, { passive: false });
        container.addEventListener('mousedown', handleMouseDown as any);

        return () => {
            window.removeEventListener('drag', handleDrag as EventListener);
            window.removeEventListener('mouseup', handleMouseUp as any);
            window.removeEventListener('mousemove', handleMouseMove as any);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);

            if (container) {
                container.removeEventListener('touchstart', handleTouchStart);
                container.removeEventListener('mousedown', handleMouseDown as any);
            }
        };
    }, [handleMouseUp, handleMouseMove, handleTouchMove, handleTouchEnd, handleTouchStart, handleMouseDown]);

    useEffect(() => {
        if (nativeMobileScroll) {
            setIsMobile(isMobileDevice());
        }
    }, [nativeMobileScroll, isMobileDevice]);

    return (
        <Component
            className={[
                className || '',
                isPressed ? 'scroll-container-dragging' : '',
                hideScrollbars ? 'scroll-container-hide-scrollbars' : '',
                isMobile ? 'scroll-container_native-scroll' : '',
            ]
                .filter(Boolean)
                .join(' ')
                .trim()}
            style={style}
            ref={containerRef}
            onScroll={handleScroll}
        >
            {children}
        </Component>
    );
});

ScrollContainer.displayName = 'ScrollContainer';

export default ScrollContainer;